import { Router } from '@angular/router';
import { AsyncHelper } from './../../framework/async-helper';
import { IdentityService } from './identity/identity.service';
import { Logger, LoggerFactory } from './logger.service';
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { Environment, ENVIRONMENT_DI } from './../environment/environment';
import { Subscription } from 'rxjs';
import { AccountService } from 'app/account/account.service';
import { Account, BasicAccount } from 'app/back-office/account.model';
import { Tier } from 'app/shared/account/tier.model';
import { CurrentSiteIdStore } from './current-site-id.store';

export class Site {
  constructor(
    public readonly id: number,
    public readonly name: string,
    public readonly isExpired: boolean,
    public readonly isPoweredByDisabled: boolean,
    public readonly previewUrl: string,
    public readonly urls: Array<string>,
    public readonly tier: Tier,
    public readonly language: string,
    public readonly isPaused = false,
  ) {}
}

/** A site from the account list. It has no urls, previewUrl or tier; read those from AccountService. */
export type SiteSummary = Pick<Site, 'id' | 'name' | 'isExpired' | 'language' | 'isPaused'>;

@Injectable()
export class SiteContextService implements OnDestroy {
  private currentSite: Site | undefined;
  private allSites = new Array<SiteSummary>();
  public isBusy = false;
  private isInitializing = false;

  private readonly logger: Logger;
  private readonly identitySubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  public readonly onCurrentSiteChanged = new EventEmitter<Site | undefined>();
  public readonly onCurrentSiteUpdated = new EventEmitter<Site | undefined>();

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly identityService: IdentityService,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
    protected readonly router: Router,
    private readonly accountService: AccountService,
    @Inject(ENVIRONMENT_DI) private readonly environment: Environment,
  ) {
    this.logger = loggerFactory.get('SiteContextService');
    this.identitySubscription = this.identityService.onIdentityChanged.subscribe(async () => {
      if (!this.isInitializing) {
        await this.initializeAsync();
      }
    });
  }

  // Locks the synchronization context until the sites are initialized and returns the current site when it's initialized
  public async getCurrentSiteAsync(): Promise<Site | undefined> {
    await this.waitUntilNotBusyAsync();
    this.ensureCurrentSiteIsAllowed();
    return this.currentSite;
  }

  public getCurrentSiteSync(): Site {
    return this.currentSite;
  }

  public async getAllSitesAsync(): Promise<Array<SiteSummary>> {
    await this.waitUntilNotBusyAsync();
    if (!this.allSites.length) {
      this.allSites = await this.getAllSitesInternalAsync();
    }
    return this.allSites;
  }

  public async getAllSitesExceptCurrentAsync(): Promise<Array<SiteSummary>> {
    await this.waitUntilNotBusyAsync();
    const allSites = await this.getAllSitesAsync();
    return allSites.filter((site) => site.id !== this.currentSite.id).sort((a, b) => a.name.localeCompare(b.name));
  }

  public async setCurrentSiteAsync(siteId: number, reload = true): Promise<void> {
    this.logger.debug(`Setting current site to: ${siteId}. Currently busy: ${this.isBusy}`);
    await this.waitUntilNotBusyAsync();
    this.isBusy = true;

    const site = await this.accountService.getAccount(siteId).toPromise();
    if (!site) {
      this.logger.error(`Site does not exist in allowed sites, therefore ${siteId} cannot be set as current site.`);
      this.isBusy = false;
      this.ensureCurrentSiteIsAllowed();
    }
    this.currentSite = this.rawAccountToSite(site);
    this.updateCurrentSiteId(this.currentSite.id);
    this.notifyNewSite();
    if (reload) {
      location.reload();
    }
    this.isBusy = false;
  }

  public setCurrentSite(account: Account) {
    this.logger.debug(`Setting current site to: ${account.siteId}`);
    this.currentSite = this.rawAccountToSite(account);
    this.updateCurrentSiteId(this.currentSite.id);
    this.notifyNewSite();
  }

  // Sets isBusy and isInitializing to true when it's operating
  public async initializeAsync() {
    await this.waitUntilNotBusyAsync();

    // Do not allow parallel initialization
    if (this.isInitializing) {
      this.logger.warn('Already initializing the sites');
      return AsyncHelper.waitUntilTrueAsync(
        () => !this.isInitializing,
        () => Promise.resolve(),
        () => Promise.reject('Cannot initialize site context.'),
      );
    }

    this.isInitializing = true;
    this.isBusy = true;
    if (!this.identityService.hasCurrentIdentity || !this.identityService.isUserVerified) {
      this.logger.warn('Cannot initialize sites as user is anonym or not verified');
      this.currentSite = undefined;
      this.isBusy = false;
      this.isInitializing = false;
      this.allSites = new Array<SiteSummary>();
      this.notifyNewSite();
      return;
    }

    let currentSiteId = this.getCurrentSiteId();
    if (!currentSiteId) {
      currentSiteId = this.identityService.currentIdentity.getAllSiteIds()[0];
      this.updateCurrentSiteId(currentSiteId);
    }

    if (!currentSiteId) {
      this.logger.warn('User has no accounts. The current site store will be cleared.');
      this.currentSiteIdStore.clear();
      this.isBusy = false;
      this.isInitializing = false;
      this.notifyNewSite();
    }

    if (currentSiteId === -1) {
      this.isBusy = false;
      this.logger.info(
        '-1 is a temporary siteId, only used on registration. It will only be -1 while the users account is being created',
      );
      return;
    }

    this.accountService.getAccount(currentSiteId).subscribe(
      async (site: Account) => {
        if (!site) {
          this.logger.warn('Current account does not exist. The current site store will be cleared.');
          this.currentSiteIdStore.clear();
          this.isBusy = false;
          this.isInitializing = false;
          this.notifyNewSite();
          return;
        } else {
          this.logger.debug(`Current account is read from cookie and will be set to ${site.name} (${site.siteId})`);
          this.currentSite = this.rawAccountToSite(site);

          // All is well, continue
          this.isBusy = false;
          this.isInitializing = false;
          this.notifyNewSite();

          return;
        }
      },
      (error) => {
        // 401/403 → sign out. 404 → account/linkage missing; clear the stale pointer. Other → transient, keep session.
        const status = error?.status;
        const isAuthError = status === 401 || status === 403;
        const isMissingAccount = status === 404;
        this.isBusy = false;
        this.isInitializing = false;
        if (isAuthError) {
          this.logger.warn(
            `Error getting current account (status=${status}, ${error?.message}). The user will be logged out.`,
          );
          this.identityService.signOut('site-context-init-auth-error');
        } else if (isMissingAccount) {
          this.logger.warn(
            `Current account ${currentSiteId} not found (404, ${error?.message}). Clearing the stale current-site pointer.`,
          );
          this.currentSite = undefined;
          this.currentSiteIdStore.clear();
        } else {
          this.logger.error(
            `Error getting current account (status=${status}, ${error?.message}). Keeping session; user can retry. Stack: ${error?.stack ?? ''}`,
          );
        }
        this.notifyNewSite();
        return;
      },
    );
  }

  public userHasMoreThanOneAccount() {
    return this.identityService.currentIdentity.getAllSiteIds().length > 1;
  }

  private rawAccountToSite(a: Account): Site {
    return new Site(
      a.siteId,
      a.name,
      a.isExpired,
      a.isPoweredByDisabled,
      a.previewUrl,
      a.urls,
      a.tier,
      a.language,
      !!a.isPaused,
    );
  }
  private notifyNewSite() {
    this.logger.debug(`Current site is changed to : ${JSON.stringify(this.currentSite)}`);
    this.onCurrentSiteChanged.emit(this.currentSite);
  }

  private basicAccountToSiteSummary(a: BasicAccount): SiteSummary {
    return { id: a.siteId, name: a.name, isExpired: a.isExpired, language: a.language, isPaused: !!a.isPaused };
  }

  private async getAllSitesInternalAsync(): Promise<SiteSummary[]> {
    if (!this.identityService.hasCurrentIdentity) {
      this.logger.debug('User could not be found, sites will not be fetched');
      return [];
    }
    if (!this.identityService.isUserVerified) {
      this.logger.debug('User is not verified, sites will not be fetched');
      return [];
    }
    if (!this.identityService.currentIdentity.email) {
      this.logger.error('Verified user has no e-mail address.');
      return [];
    }

    // update jwt before get sites collection
    if (!this.environment.useMocks /* mock framework does not care about identity */) {
      await this.identityService.requestUpdateIdentityPayloadAsync();
    }

    // Get sites from account-settings
    const allSites = new Array<SiteSummary>();
    const allAccounts = await this.accountService.getAllAccounts().toPromise();
    allAccounts.forEach((account) => {
      allSites.push(this.basicAccountToSiteSummary(account));
    });

    if (
      !this.environment.isProduction &&
      !this.environment.useMocks /* mock framework does not care about identity */
    ) {
      if (!this.areRespondedSitesSane(allSites)) {
        this.logger.warn('Sanity check has failed, the application will not function as expected');
        return [];
      }
    }
    return allSites;
  }

  private ensureCurrentSiteIsAllowed(): boolean {
    if (this.currentSite && this.identityService.currentIdentity) {
      const allowedSiteIds = this.identityService.currentIdentity.getAllSiteIds();
      const isSiteAllowed = allowedSiteIds.find((s) => s === this.currentSite.id);
      if (!isSiteAllowed) {
        this.logger.error(`Current site does not exist in JWT, therefore ${this.currentSite.id} \n
              cannot be set as current site. Allowed sites : ${JSON.stringify(allowedSiteIds)}`);
        // If current site does not exist in allowed sites, something terribly went wrong,
        // so the user is logged out aggressively as the functionality of the site would fail terribly.
        this.identityService.signOut('site-not-allowed-in-jwt');
        // Even get rid of the cookie
        this.currentSiteIdStore.clear();
        return false;
      }
    }
    return true;
  }

  public getCurrentSiteId(): number | undefined {
    return this.currentSiteIdStore.get();
  }

  public updateCurrentSiteId(siteId: number): void {
    if (siteId <= 0) {
      this.logger.error('Site id cannot be lower than zero');
    }
    if (this.getCurrentSiteId() === siteId) {
      return;
    }
    this.logger.info(`Switching site id to ${siteId}`);
    this.currentSiteIdStore.set(siteId);
  }

  public updateCurrentSiteIdAtLogin(): void {
    const siteIds = this.identityService.currentIdentity?.getAllSiteIds();
    const siteId = siteIds?.[0];
    if (!siteId) return;
    this.logger.info(`Switching site id to ${siteId}`);
    this.currentSiteIdStore.set(siteId);
  }
  private waitUntilNotBusyAsync(): Promise<void> {
    if (!this.isBusy) {
      return;
    }
    return AsyncHelper.waitUntilTrueAsync(
      () => !this.isBusy,
      () => Promise.resolve(),
      () => Promise.reject('Cannot initialize site context.'),
    );
  }
  private areRespondedSitesSane(responseData: SiteSummary[]): boolean {
    if (!this.identityService.hasCurrentIdentity) {
      this.logger.warn('The user just got logged off before being able to initialize sites');
      return false;
    }
    if (this.identityService.currentIdentity.isAdmin()) {
      return true;
    }
    const identitySiteIds = this.identityService.currentIdentity.getAllSiteIds();
    const returnedSiteIds = responseData.map((s) => s.id);
    if (!this.areIdentical(identitySiteIds, returnedSiteIds)) {
      this.logger.error(`A logical conflict is detected. The sites returned from Triggerbee.Api does not match
                               The site information that exists in Identity.
                               Site id's in identity : ${identitySiteIds.join(
                                 ',',
                               )}, returned site id's ${returnedSiteIds.join(',')}
                               `);
      return false;
    }
    return true;
  }
  private areIdentical<T>(first: Array<T>, second: Array<T>): boolean {
    if (first.sort().join(',') === second.sort().join(',')) {
      return true;
    }
    return false;
  }

  ngOnDestroy() {
    this.subscriptionArray.push(this.identitySubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
